import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { logAudit } from "@/lib/audit";
import { useAuth } from "@/context/AuthContext";
import { useAppSettings } from "@/context/AppSettingsContext";
import { useToast } from "@/context/ToastContext";
import { TextField, SelectField, TextAreaField, ToggleSwitch } from "@/components/base/FormControls";

interface FiscalForm {
  enabled: boolean;
  business_name: string;
  rnc: string;
  receipt_type: string;
  ncf_prefix: string;
  ncf_sequence: string;
  environment: string;
  provider: string;
  status: string;
  notes: string;
}

const RECEIPT_TYPES = [
  { value: "01", label: "01 - Comprobante de Crédito Fiscal" },
  { value: "02", label: "02 - Comprobante de Consumo" },
  { value: "31", label: "31 - Comprobante de Compras" },
  { value: "32", label: "32 - Nota de Crédito" },
  { value: "33", label: "33 - Nota de Débito" },
  { value: "34", label: "34 - Comprobante de Retención" },
  { value: "e31", label: "e31 - e-CF Factura de Crédito Fiscal" },
  { value: "e32", label: "e32 - e-CF Factura de Consumo" },
];

export default function FiscalTab() {
  const toast = useToast();
  const { profile } = useAuth();
  const { settings, fiscal, settingsId, refresh } = useAppSettings();
  const [form, setForm] = useState<FiscalForm>({
    enabled: false,
    business_name: "",
    rnc: "",
    receipt_type: "02",
    ncf_prefix: "B",
    ncf_sequence: "1",
    environment: "test",
    provider: "",
    status: "No configurado",
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({
      enabled: fiscal.enabled ?? false,
      business_name: fiscal.business_name ?? settings.company_name ?? "",
      rnc: fiscal.rnc ?? settings.rnc ?? "",
      receipt_type: fiscal.receipt_type ?? "02",
      ncf_prefix: fiscal.ncf_prefix ?? "B",
      ncf_sequence: String(fiscal.ncf_sequence ?? 1),
      environment: fiscal.environment ?? "test",
      provider: fiscal.provider ?? "",
      status: fiscal.status ?? "No configurado",
      notes: fiscal.notes ?? "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fiscal, settings.company_name, settings.rnc]);

  const update = <K extends keyof FiscalForm>(key: K, value: FiscalForm[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        fiscal: {
          enabled: form.enabled,
          business_name: form.business_name.trim() || null,
          rnc: form.rnc.trim() || null,
          receipt_type: form.receipt_type,
          ncf_prefix: form.ncf_prefix.trim() || null,
          ncf_sequence: Number.parseInt(form.ncf_sequence, 10) || 1,
          environment: form.environment,
          provider: form.provider.trim() || null,
          status: form.status,
          notes: form.notes.trim() || null,
        },
        updated_at: new Date().toISOString(),
      };

      const query = settingsId
        ? supabase.from("app_settings").update(payload).eq("id", settingsId)
        : supabase.from("app_settings").insert(payload);
      const { error } = await query;
      if (error) {
        toast.error("No se pudo guardar la configuración fiscal.");
        return;
      }

      await logAudit({
        userId: profile?.id ?? null,
        userName: `${profile?.full_name ?? ""} ${profile?.last_name ?? ""}`.trim() || "Usuario",
        action: "configurar",
        entity: "app_settings",
        entityId: settingsId ?? "app_settings",
        newValue: { fiscal_enabled: form.enabled, fiscal_environment: form.environment },
      });

      await refresh();
      toast.success("Configuración fiscal guardada.");
    } catch {
      toast.error("Ocurrió un error inesperado.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-start gap-3 rounded-lg border border-accent-300 bg-accent-50 p-4 text-sm text-accent-900">
        <span className="mt-0.5 flex h-5 w-5 items-center justify-center">
          <i className="ri-information-line text-base"></i>
        </span>
        <p className="flex-1 leading-snug">
          Este módulo prepara la <strong>facturación fiscal (DGII / e-CF)</strong>. No realiza
          certificaciones ni envíos reales: la estructura queda lista para conectar el proveedor
          fiscal cuando esté disponible, sin reconstruir la facturación.
        </p>
      </div>

      <section className="flex flex-col gap-5 rounded-lg border border-background-200 bg-background-100 p-5">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-100 text-primary-700">
            <i className="ri-government-line text-base"></i>
          </span>
          <h3 className="font-heading text-sm font-semibold text-foreground-900">Datos fiscales</h3>
        </div>

        <ToggleSwitch
          label="Activar facturación fiscal"
          description="Habilita los campos de NCF/e-CF en las facturas."
          checked={form.enabled}
          onChange={(checked) => update("enabled", checked)}
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextField
            label="Razón social"
            name="business_name"
            value={form.business_name}
            onChange={(value) => update("business_name", value)}
            placeholder="Nombre o razón social"
          />
          <TextField
            label="RNC"
            name="rnc"
            value={form.rnc}
            onChange={(value) => update("rnc", value)}
            placeholder="1-01-00000-1"
          />
          <SelectField
            label="Tipo de comprobante"
            name="receipt_type"
            value={form.receipt_type}
            onChange={(value) => update("receipt_type", value)}
            options={RECEIPT_TYPES}
          />
          <SelectField
            label="Ambiente"
            name="environment"
            value={form.environment}
            onChange={(value) => update("environment", value)}
            options={[
              { value: "test", label: "Pruebas" },
              { value: "production", label: "Producción" },
            ]}
          />
          <TextField
            label="Prefijo NCF"
            name="ncf_prefix"
            value={form.ncf_prefix}
            onChange={(value) => update("ncf_prefix", value.toUpperCase())}
            placeholder="B"
          />
          <TextField
            label="Secuencia NCF"
            name="ncf_sequence"
            value={form.ncf_sequence}
            onChange={(value) => update("ncf_sequence", value.replace(/[^0-9]/g, ""))}
            inputMode="numeric"
            placeholder="1"
          />
          <TextField
            label="Proveedor fiscal / API"
            name="provider"
            value={form.provider}
            onChange={(value) => update("provider", value)}
            placeholder="Nombre del proveedor"
            hint="Se conectará más adelante. No se guardan credenciales aquí."
          />
          <SelectField
            label="Estado fiscal"
            name="status"
            value={form.status}
            onChange={(value) => update("status", value)}
            options={[
              { value: "No configurado", label: "No configurado" },
              { value: "Preparado", label: "Preparado" },
              { value: "Activo", label: "Activo" },
            ]}
          />
        </div>

        <TextAreaField
          label="Notas fiscales"
          name="notes"
          value={form.notes}
          onChange={(value) => update("notes", value)}
          placeholder="Observaciones internas sobre la facturación fiscal."
          rows={3}
        />
      </section>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-70"
        >
          {saving ? (
            <i className="ri-loader-4-line animate-spin text-base"></i>
          ) : (
            <i className="ri-save-3-line text-base"></i>
          )}
          Guardar cambios
        </button>
      </div>
    </div>
  );
}